import { useState } from "react";

import { Textarea } from "@/web/components/primitives/textarea";
import { useProcedureMutation } from "@/web/lib/tanstack";
import { procedures } from "@tycho/web-bindings";
import { ActionCard } from "@/web/routes/_protected/smb/-dashboard/-ActionCard";
import { QuestionDetailsContent } from "@/web/routes/_protected/smb/-dashboard/-questions/QuestionDetailsContent";
import { QuestionSubtitleMarkdown } from "@/web/routes/_protected/smb/-dashboard/-questions/QuestionSubtitleMarkdown";
import type {
  SmbActionCoreProps,
  SmbActionDefinition,
  SmbQuestionDetails,
} from "@/web/routes/_protected/smb/-dashboard/-types";

export type FreeformTextQuestionProps = {
  questionId: string;
  badge: string;
  question: string;
  subtitle?: string;
  details?: SmbQuestionDetails;
};

function FreeformTextQuestion({
  questionId,
  badge,
  question,
  subtitle,
  details,
  onSkip,
  onComplete,
}: FreeformTextQuestionProps & SmbActionCoreProps) {
  const [answer, setAnswer] = useState("");

  const { mutate: submitAnswer } = useProcedureMutation(
    procedures.submitUnderwritingQuestionAnswer,
  );

  const canSubmit = answer.trim().length > 0;

  const handleContinue = () => {
    // TODO(fas): error handling?
    submitAnswer({
      answer: { tag: "Freeform", value: { value: answer.trim(), note: undefined } },
      questionId: questionId,
    });
    onComplete({ answer: answer.trim(), note: undefined });
  };

  return (
    <ActionCard category="budget">
      <ActionCard.Header>
        <ActionCard.Badge>{badge}</ActionCard.Badge>
        {details && <ActionCard.DetailsTrigger />}
      </ActionCard.Header>

      <ActionCard.Main>
        <ActionCard.Title>{question}</ActionCard.Title>
        {subtitle && (
          <ActionCard.Description>
            <QuestionSubtitleMarkdown markdown={subtitle} />
          </ActionCard.Description>
        )}
      </ActionCard.Main>

      <ActionCard.Body className="flex flex-col">
        <Textarea
          value={answer}
          onChange={(e) => setAnswer(e.target.value)}
          placeholder="Add your answer here"
          className="h-[155px] flex-1 resize-none border border-withus-white/30 bg-white/5 p-4 text-withus-white placeholder:text-withus-white/40 focus-visible:border-withus-white focus-visible:ring-0"
        />
      </ActionCard.Body>

      <ActionCard.Footer>
        <ActionCard.SkipButton onSkip={onSkip} />
        {canSubmit && (
          <ActionCard.PrimaryButton onClick={handleContinue}>Continue</ActionCard.PrimaryButton>
        )}
      </ActionCard.Footer>

      {details && (
        <ActionCard.Back
          title={details.title}
          description={
            details.subtitle ? <QuestionSubtitleMarkdown markdown={details.subtitle} /> : undefined
          }
        >
          <QuestionDetailsContent details={details} />
        </ActionCard.Back>
      )}
    </ActionCard>
  );
}

export function createFreeformTextQuestion(
  props: FreeformTextQuestionProps & { id?: string; canSkip?: boolean },
): SmbActionDefinition {
  const { id = props.questionId, canSkip = true, ...rest } = props;
  return {
    id,
    category: "budget",
    canSkip,
    render: (core) => <FreeformTextQuestion {...rest} {...core} />,
  };
}
